import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminDeleteMedicinePackImage, adminGetMedicinePackImages, adminUpdateMedicinePackImages, uploadFiles } from "../../../api";
import { colors } from "../../../styles";
import { Box, Button, HStack, Image, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, SimpleGrid, Spinner, Text, VStack, useToast } from "@chakra-ui/react";
import { IAdminMedicinePackImages } from "../../../types";
import MyIcon from "../../../components/Icon/MyIcon";
import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, rectSortingStrategy, SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface PackImageEditModalProps {
    isOpen: boolean;
    onClose: () => void;

    medicineId: string;
}

const MAX_PACK_IMAGES = 10;

/* Sortable Pack Image Card */

interface SortablePackImageCardProps {
    dndId: string;
    packImage: string;
    onDelete: () => void;
    isPending: boolean;
}

function SortablePackImageCard({ dndId, packImage, onDelete, isPending }: SortablePackImageCardProps) {

    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: dndId, disabled: isPending });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <Box
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            position={"relative"}
            border={`1px solid ${colors.g5}`}
            borderRadius={12}
            padding={2}
            backgroundColor={colors.white}
            cursor={isPending ? "not-allowed" : "grab"}
        >
            <Image
                src={packImage}
                alt="pack-image"
                width={"100%"}
                height={"140px"}
                objectFit={"contain"}
                borderRadius={8}
                backgroundColor={colors.white}
                draggable={false}
            />
            <Button
                size={"xs"}
                position={"absolute"}
                top={-3}
                right={-3}
                minWidth={"32px"}
                height={"32px"}
                borderRadius={"full"}
                backgroundColor={colors.g5}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={onDelete}
                isDisabled={isPending}
                isLoading={isPending}
            >
                <MyIcon
                    src={"/svg/delete-g1.svg"}
                    alt="delete"
                    width={16}
                    height={16}
                />
            </Button>
        </Box>
    );
}

export default function PackImageEditModal(props: PackImageEditModalProps) {

    /* query */

    const queryClient = useQueryClient();
    const toast = useToast();
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const { data: packImageData, isLoading: isPackImageDataLoading } = useQuery<IAdminMedicinePackImages>({
        queryKey: ['adminGetMedicinePackImage', props.medicineId],
        queryFn: () => adminGetMedicinePackImages({ medicineId: props.medicineId }),
        enabled: !!props.medicineId && props.isOpen,
    });

    /* draft pack images */

    const [draftPackImages, setDraftPackImages] = useState<string[]>([]);

    useEffect(() => {
        if (!props.isOpen) return;
        setDraftPackImages(packImageData?.packImages ?? []);
    }, [packImageData?.packImages, props.isOpen]);

    /* mutations */

    const deletePackImageMutation = useMutation({
        mutationFn: adminDeleteMedicinePackImage,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminGetMedicinePackImage', props.medicineId] });
        },
    });

    const updatePackImagesMutation = useMutation({
        mutationFn: adminUpdateMedicinePackImages,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminGetMedicinePackImage', props.medicineId] });
        },
    });

    /* pending */

    const [isUploading, setIsUploading] = useState(false);
    const isPending = deletePackImageMutation.isPending || updatePackImagesMutation.isPending || isUploading;

    /* delete handlers */

    const onDeletePackImage = async (packImage: string, index: number) => {

        if (isPending) return;

        setDraftPackImages((prev) => prev.filter((_, i) => i !== index));

        await deletePackImageMutation.mutateAsync({ medicineId: props.medicineId, packImage });
    };

    /* upload handlers */

    const onClickUpload = () => {
        if (isPending) return;
        fileInputRef.current?.click();
    };

    const onSelectFiles = async (event: ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = Array.from(event.target.files ?? []);
        event.target.value = "";

        if (!selectedFiles.length || isPending) return;
        if (isDirty) {
            toast({
                title: "순서 변경 사항을 먼저 저장한 후 업로드해 주세요.",
                status: "warning",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
            return;
        }

        const imageFiles = selectedFiles.filter((file) => file.type.startsWith("image/"));
        if (imageFiles.length !== selectedFiles.length) {
            toast({
                title: "이미지 파일만 업로드할 수 있습니다.",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
            return;
        }

        if (draftPackImages.length + imageFiles.length > MAX_PACK_IMAGES) {
            toast({
                title: `포장 이미지는 최대 ${MAX_PACK_IMAGES}장까지 등록할 수 있습니다.`,
                status: "warning",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
            return;
        }

        try {
            setIsUploading(true);
            const uploadedUrls = await uploadFiles({ type: "medicine", id: props.medicineId, files: imageFiles });
            const nextPackImages = [...draftPackImages, ...uploadedUrls];
            await updatePackImagesMutation.mutateAsync({
                medicineId: props.medicineId,
                packImages: nextPackImages,
            });
            setDraftPackImages(nextPackImages);
            toast({
                title: `${uploadedUrls.length}장의 이미지를 업로드하고 저장했습니다.`,
                status: "success",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        } catch (error) {
            console.error(error);
            toast({
                title: "이미지 업로드에 실패했습니다.",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        } finally {
            setIsUploading(false);
        }
    };

    /* drag and drop handlers */

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

    const dndIds = useMemo(
        () => draftPackImages.map((packImage, index) => `${index}::${packImage}`),
        [draftPackImages]
    );

    const isDirty = useMemo(() => {
        const source = packImageData?.packImages ?? [];
        if (source.length !== draftPackImages.length) return true;
        return source.some((value, index) => value !== draftPackImages[index]);
    }, [draftPackImages, packImageData?.packImages]);

    const onDragEnd = (event: DragEndEvent) => {
        if (isPending) return;
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = dndIds.findIndex((id) => id === String(active.id));
        const newIndex = dndIds.findIndex((id) => id === String(over.id));

        if (oldIndex < 0 || newIndex < 0) return;

        setDraftPackImages((prev) => arrayMove(prev, oldIndex, newIndex));
    };

    const onSaveOrder = async () => {
        if (isPending || !isDirty) return;
        if (draftPackImages.length > MAX_PACK_IMAGES) {
            toast({
                title: `포장 이미지는 최대 ${MAX_PACK_IMAGES}장까지 저장할 수 있습니다.`,
                status: "warning",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
            return;
        }
        await updatePackImagesMutation.mutateAsync({
            medicineId: props.medicineId,
            packImages: draftPackImages,
        });
    };

    /* render */

    return (
        <Modal motionPreset="slideInBottom" size={"4xl"} isOpen={props.isOpen} onClose={props.onClose}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <Text 
                        color={colors.black} 
                        fontSize={24} 
                        fontWeight={400}
                    >
                        {packImageData?.nameKor}
                    </Text>
                    <ModalCloseButton />
                </ModalHeader>
                <ModalBody paddingTop={5}>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        style={{ display: "none" }}
                        onChange={onSelectFiles}
                    />

                    <HStack width={"100%"} justifyContent={"space-between"} alignItems={"center"} marginBottom={10}>
                        <Button
                            size={"md"}
                            borderRadius={"full"}
                            backgroundColor={colors.blue}
                            color={colors.white}
                            fontSize={14}
                            fontWeight={400}
                            onClick={onClickUpload}
                            isDisabled={isPending}
                            isLoading={isUploading}
                        >
                            <MyIcon src={"/svg/plus.svg"} alt="add" width={12} height={12} />
                        </Button>
                        <Button
                            size={"md"}
                            borderRadius={"full"}
                            backgroundColor={colors.g1}
                            color={colors.white}
                            fontSize={14}
                            fontWeight={400}
                            onClick={onSaveOrder}
                            isDisabled={!isDirty || isPending}
                            isLoading={updatePackImagesMutation.isPending}
                        >
                            ✓
                        </Button>
                    </HStack>

                    {isPackImageDataLoading && (
                        <VStack width={"100%"} justifyContent={"center"} alignItems={"center"} paddingY={10}>
                            <Spinner color={colors.blue} />
                        </VStack>
                    )}

                    {!isPackImageDataLoading && (
                        <VStack width={"100%"} alignItems={"stretch"} paddingBottom={5}>
                            {(!packImageData?.packImages || packImageData.packImages.length === 0) && (
                                <Text fontSize={14} fontWeight={400} color={colors.g2}>등록된 포장 이미지가 없습니다.</Text>
                            )}

                            {draftPackImages.length > 0 && (
                                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                                    <SortableContext items={dndIds} strategy={rectSortingStrategy}>
                                        <SimpleGrid columns={5} spacing={4} width={"100%"}>
                                            {draftPackImages.map((packImage, index) => (
                                                <SortablePackImageCard
                                                    key={dndIds[index]}
                                                    dndId={dndIds[index]}
                                                    packImage={packImage}
                                                    onDelete={() => onDeletePackImage(packImage, index)}
                                                    isPending={isPending}
                                                />
                                            ))}
                                        </SimpleGrid>
                                    </SortableContext>
                                </DndContext>
                            )}
                        </VStack>
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    )
}